import React from "react";

import * as Sentry from "@sentry/browser";

import * as recipes from "@client/features/recipes";
import * as search from "@client/features/search";

import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";

import {useStage} from "@client/components/Stage";
import * as forms from "@client/site/forms";
import {submitForm} from "@client/site/forms";
import {server} from "@reactivated";

const IMPORT_PLACEHOLDER = `1 cup white sugar
1/2 cup unsalted butter
2 large eggs
2 teaspoons vanilla extract
1.5 cups all-purpose flour
1.5 teaspoons baking powder
1/2 cup milk
`;

// Or https://www.allrecipes.com/recipe/17481/simple-white-cake/

export const CreateRecipe = router.CreateRecipe.withHooks(() => {
    const [importValues, setImportValues] = React.useState({name: "", description: ""});

    const importForm = forms.usePform(
        {
            description: {
                type: "textarea",
                placeholder: IMPORT_PLACEHOLDER,
            },
            name: {
                type: "text",
                label: "Name your recipe",
            },
        },
        importValues,
        setImportValues,
    );

    const [route, setRoute] = React.useState<"import" | "confirm">("import");
    const stage = useStage<server.journal.nutrition.ExpandedEntry.output>({
        entries: [],
        target: null,
    });

    return {recipeForm: recipes.useRecipeForm({}), importForm, stage, route, setRoute};
})
    .withTab(
        {name: "index", title: "Manual", icon: "edit"},
        (props) => props.recipeForm.layout.component,
    )
    .withTab({name: "import", title: "Import", icon: "0758-download2"}, (props) => (
        <div className="space-y-2">
            {props.route == "import" ? (
                <div className="space-y-2">
                    <h2 className="text-xl">
                        Recipe importer<sup className="ml-1 text-indigo-800">beta</sup>
                    </h2>
                    <p>You can add instructions and portions later.</p>
                    <p>
                        This is an{" "}
                        <span className="font-medium text-indigo-800">
                            experimental
                        </span>{" "}
                        feature. Be patient.
                    </p>
                    <p>
                        If any ingredients look off, just skip them and manually fix
                        them afterwards
                    </p>
                    <h2 className="text-lg">List out your recipe ingredients</h2>
                    <forms.PWidget field={props.importForm.fields.description} />
                </div>
            ) : (
                <div className="space-y-2">
                    <h2 className="text-lg">Name your recipe</h2>
                    <forms.PWidget field={props.importForm.fields.name} />
                    <h2 className="text-lg">And confirm recipe ingredients</h2>
                    {props.stage.entries}
                </div>
            )}
        </div>
    ))
    .withActions((props) => {
        if (props.currentTab === "import") {
            return props.route == "import"
                ? [
                      {
                          icon: "0758-download2",
                          title: "Import",
                          disabled: props.importForm.values.description == "",
                          onClick: async () => {
                              const result =
                                  await server.journal.nutritional_ai.import_recipe({
                                      description: props.importForm.values.description,
                                  });
                              const {stage} = props;

                              if (result.type == "success") {
                                  void analytics.track("ANALYZED_RECIPE");
                                  props.setRoute("confirm");

                                  stage.setEntries(result.data.entries);
                              } else {
                                  void analytics.track("ERROR_ANALYZING_RECIPE");
                                  props.notify(
                                      "Error processing recipe.\n\nTry again.\n\nOur team has been notified.",
                                      {type: "error", permanent: true},
                                  );
                                  Sentry.captureMessage("Error processing image");
                              }
                          },
                      },
                  ]
                : [
                      {
                          icon: "repeat",
                          title: "Start Over",
                          onClick: () => {
                              void analytics.track("RESTARTED_RECIPE_ANALYSIS");
                              props.setRoute("import");
                          },
                      },
                      {
                          icon: "save",
                          title: "Create",
                          onClick: async () => {
                              const recipeResult =
                                  await server.journal.nutrition.create_recipe({
                                      name: props.importForm.values.name,
                                      container_servings: null,
                                      container_quantity: null,
                                      serving_size_unit: "",
                                      container_unit: "",
                                      notes: "",
                                  });

                              const stageResult = props.stage.validate();

                              if (
                                  stageResult == null ||
                                  recipeResult.type != "success"
                              ) {
                                  throw new Error("Failed to create recipe");
                              }

                              const createdRecipe = recipes.addEntriesToRecipe({
                                  recipe: recipeResult.data,
                                  entries: stageResult.entries,
                              });

                              router.push(
                                  "update_recipe.recipe",
                                  {
                                      recipeId: createdRecipe.id,
                                  },
                                  {update_recipe: createdRecipe},
                              );
                          },
                      },
                  ];
        }

        return [
            {
                icon: "save",
                title: "Save",
                onClick: async () => {
                    const result = await submitForm(
                        server.journal.nutrition.create_recipe,
                        props.recipeForm.form.values,
                        props.recipeForm.form,
                    );
                    if (result.type === "success") {
                        void props.dispatch(search.loadSearch({reset: true}));
                        props.notify(
                            "Your recipe was created. Now add some ingredients.",
                        );
                        router.push("update_recipe.recipe", {recipeId: result.data.id});
                    }
                },
            },
        ];
    })
    .withOptions(() => ({
        title: "Create recipe",
        back: "Recipes",
    }));
